import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

const NotFoundPage = () => {
  useSEO({
    title: 'Página no encontrada - J&D Propiedad Raíz',
    description: 'La página que buscas no existe. Regresa al inicio para encontrar lo que necesitas.',
    keywords: 'error 404, página no encontrada, J&D Propiedad Raíz',
    ogTitle: 'Página no encontrada - J&D Propiedad Raíz',
    ogDescription: 'La página que buscas no existe.',
    ogImage: 'https://jdpropiedadraiz.co/logo192.png'
  });

  return (
    <div className="min-h-screen bg-brand-black text-brand-white flex items-center justify-center relative overflow-hidden">
      
      {/* Background with animated elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-black via-brand-black to-brand-gold/5 animate-pulse"></div>
        
        {/* Floating logo watermarks */}
        <div className="absolute top-20 left-10 opacity-5 transform rotate-12 animate-bounce">
          <img src="/logo192.png" alt="" className="w-32 h-32 filter brightness-0 invert" />
        </div>
        <div className="absolute top-1/3 right-20 opacity-3 transform -rotate-12 animate-pulse">
          <img src="/logo192.png" alt="" className="w-24 h-24 filter brightness-0 invert" />
        </div>
        <div className="absolute bottom-1/4 left-1/4 opacity-4 transform rotate-45 animate-bounce" style={{animationDelay: '1s'}}>
          <img src="/logo192.png" alt="" className="w-20 h-20 filter brightness-0 invert" />
        </div>
      </div>

      {/* Main content */}
      <div className="text-center max-w-2xl mx-auto px-6 relative z-10">
        
        {/* 404 Number */}
        <div className="mb-8">
          <h1 className="text-9xl md:text-[12rem] font-bold text-brand-gold opacity-20 leading-none">
            404
          </h1>
        </div>

        {/* Error message */}
        <div className="mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-brand-gold mb-4">
            Página no encontrada
          </h2>
          <p className="text-xl md:text-2xl text-brand-gray leading-relaxed">
            Lo sentimos, la página que buscas no existe o ha sido movida.
          </p>
        </div>

        {/* Suggestions */}
        <div className="bg-brand-gold/10 border border-brand-gold/20 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h3 className="text-2xl font-bold text-brand-gold mb-4">
            ¿Qué puedes hacer?
          </h3>
          <ul className="text-brand-gray space-y-2 text-left max-w-md mx-auto">
            <li className="flex items-start">
              <span className="text-brand-gold mr-3 mt-1">•</span>
              <span>Verifica que la URL esté escrita correctamente</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-gold mr-3 mt-1">•</span>
              <span>Regresa a la página principal</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-gold mr-3 mt-1">•</span>
              <span>Explora nuestras propiedades disponibles</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-gold mr-3 mt-1">•</span>
              <span>Contacta a nuestro equipo de asesores</span>
            </li>
          </ul>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/" 
            className="bg-brand-gold text-brand-black px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold/90 transition-all duration-300 transform hover:scale-105 inline-block text-center hover:shadow-lg hover:shadow-brand-gold/25"
          >
            🏠 Ir al Inicio
          </Link>
          
          <Link 
            to="/nosotros" 
            className="border-2 border-brand-gold text-brand-gold px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold hover:text-brand-black transition-all duration-300 inline-block text-center hover:shadow-lg hover:shadow-brand-gold/25"
          >
            📋 Conocer Más
          </Link>
          
          <a 
            href="https://wa.me/573102390002?text=Hola,%20necesito%20ayuda%20para%20encontrar%20una%20página%20en%20su%20sitio%20web"
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-brand-gold text-brand-gold px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold hover:text-brand-black transition-all duration-300 inline-flex items-center justify-center hover:shadow-lg hover:shadow-brand-gold/25"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515z"/>
            </svg>
            💬 Contactar
          </a>
        </div>

        {/* Back button */}
        <div className="mt-8">
          <button 
            onClick={() => window.history.back()} 
            className="text-brand-gray hover:text-brand-gold transition-colors duration-300 underline"
          >
            ← Regresar a la página anterior
          </button>
        </div>

      </div>
    </div>
  );
};

export default NotFoundPage;
